import { createContext, useCallback, useContext, useState } from 'react';
import {
  SectionState,
  SectionStateContextType,
  SectionStateProviderProps,
} from './util';

const SectionStateContext = createContext<SectionStateContextType | undefined>(
  undefined
);

export function SectionStateProvider({ children }: SectionStateProviderProps) {
  const [sections, setSections] = useState<Record<string, SectionState>>({});

  const toggleSection = useCallback((sectionId: string) => {
    setSections((prev) => {
      const currentSection = prev[sectionId];
      const isCurrentlyDetached = currentSection?.isDetached || false;

      return {
        ...prev,
        [sectionId]: {
          ...currentSection,
          id: sectionId,
          isDetached: !isCurrentlyDetached,
        },
      };
    });
  }, []);

  const updateSectionPosition = useCallback(
    (sectionId: string, position: { top: number; left: number }) => {
      setSections((prev) => {
        const currentSection = prev[sectionId];
        return {
          ...prev,
          [sectionId]: {
            ...currentSection,
            id: sectionId,
            isDetached: currentSection?.isDetached || false,
            position,
          },
        };
      });
    },
    []
  );

  return (
    <SectionStateContext.Provider
      value={{ sections, toggleSection, updateSectionPosition }}
    >
      {children}
    </SectionStateContext.Provider>
  );
}

export function useSectionState() {
  const context = useContext(SectionStateContext);
  if (!context) {
    throw new Error(
      'useSectionState must be used within a SectionStateProvider'
    );
  }
  return context;
}
